import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import useQuery from "../search/useQuery";
import QUERY_TAGS from "../search/queryTags";
import { fetchMyTransactions } from "./financeService";
import { EMPTY_PAGE, normalizeSharedPage } from "./financePage";
import { mapTransactions } from "./financeMappers";
import { VOLATILE_STALE_TIME } from "./financeCache";

export const PAGE_STEP = 20;

const useTransactions = ({ purpose, pageSize = PAGE_STEP, enabled = true } = {}) => {
  const [size, setSize] = useState(pageSize);
  const settledRef = useRef(EMPTY_PAGE);

  useEffect(() => {
    setSize(pageSize);
    settledRef.current = EMPTY_PAGE;
  }, [purpose, pageSize]);

  const query = useQuery({
    key: enabled ? `finance:transactions:${purpose || "all"}:${size}` : null,
    tag: QUERY_TAGS.PAYMENT_TRANSACTIONS,
    staleTime: VOLATILE_STALE_TIME,
    enabled,
    fetcher: () => fetchMyTransactions({ page: 1, pageSize: size, purpose }),
  });

  const fresh = useMemo(() => normalizeSharedPage(query.currentData), [query.currentData]);
  const hasFresh = query.currentData !== undefined;
  const page = hasFresh ? fresh : settledRef.current;

  useEffect(() => {
    if (hasFresh) settledRef.current = fresh;
  }, [hasFresh, fresh]);

  const items = useMemo(() => mapTransactions(page.items), [page]);

  const loadMore = useCallback(() => {
    setSize((current) => current + PAGE_STEP);
  }, []);

  const hasData = hasFresh || settledRef.current !== EMPTY_PAGE;

  return {
    items,
    total: page.total,
    isLoading: !hasData && !query.isError && query.enabled,
    isFetching: query.isFetching,
    isPaging: query.isFetching && hasData,
    isError: query.isError && !hasData,
    error: query.error,
    canLoadMore: !page.isLast && !query.isFetching && !query.isError,
    loadMore,
    refetch: query.refetch,
  };
};

export default useTransactions;
